import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const THEME_KEY = 'theme'

// O portão `?dark=1` foi removido em 05/08/2026 — o tema saiu do rollout
// controlado e a lua vale pra todo mundo, nos três apps. Duas coisas que
// vieram junto e não devem voltar: a flag grudava na URL e era arrastada por
// TODA navegação da SPA (`/?dark=1&panel=whatsapp`), e a lua ficava invisível
// pra quem nunca tinha aberto com a flag — o que lia como "o tema quebrou".
// O `index.html` limpa a flag antiga da URL e a chave `theme:unlocked` órfã.

// Lê o tema salvo. Sem valor gravado o default é CLARO — de propósito: NÃO
// seguimos o prefers-color-scheme do sistema operacional, senão quem usa o SO
// no escuro entraria no tema novo sem ter escolhido. O `index.html` tem a
// lógica espelhada num script inline (anti-flash); se um mudar, o outro muda.
function themeSalvo(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(themeSalvo)

  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)
    try { localStorage.setItem(THEME_KEY, theme) } catch { /* storage bloqueado */ }
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
